<template>
  <v-container fluid class="py-6 px-2 px-md-6">
    <!-- Contenedor principal con bordes redondeados -->
    <v-card class="pa-6 global-dashboard-card">
      <v-card-title class="text-h6 font-weight-bold">Vista generalizada de clientes</v-card-title>
      <v-divider class="mb-4"></v-divider>

      <!-- Contenido -->
      <v-row class="content-row">
        <!-- Árbol -->
        <v-col cols="12" md="4" class="left-pane">
          <div class="tree-scroll">
            <v-treeview
              :items="treeItems"
              open-on-click
              item-key="id"
              item-text="name"
              dense
              :open.sync="open"
              :indent-lines="true"
              @update:open="onOpenUpdate"
              :load-children="loadChildren"
              class="pa-2 flex-grow-1"
              :style="treeInlineStyle"
            >
              <template v-slot:prepend="{ item, open }">
                <v-icon small>{{ getIcon(item, open) }}</v-icon>
              </template>
              <template v-slot:label="{ item }">
                <span
                  v-if="item.type === 'project'"
                  class="tree-label tree-label--project"
                  :class="{ 'tree-label--loading': cargandoTabla && selectedProjectKey === projectKey(item) }"
                  @click.stop.prevent="onProjectClick(item)"
                >
                  {{ item.name }}
                </span>
                <span v-else>{{ item.name }}</span>
              </template>
            </v-treeview>
          </div>
        </v-col>

        <!-- Tabla -->
        <v-col cols="12" md="8" class="right-pane d-flex flex-column min-h-0">
          <v-data-table
            :headers="headers"
            :items="gabinetesSeleccionados"
            class="elevation-1 flex-grow-1 white-card"
            fixed-header
            :height="paneHeight"
            disable-pagination
            hide-default-footer
            @click:row="onRowClick"
            :loading="cargandoTabla"
            loading-text="Cargando gabinetes..."
            item-key="id"
          >
            <template v-slot:no-data>
              <v-alert type="info" class="ma-4" dense text>
                <span v-if="!proyectoSeleccionado">Por favor selecciona un proyecto.</span>
                <span v-else>No hay gabinetes para este proyecto.</span>
              </v-alert>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Volver -->
      <v-card-actions class="justify-end mt-4">
        <v-btn color="grey darken-3" @click="volverInicio">
          <v-icon left>mdi-home</v-icon>
          Volver a Inicio
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios'
import API_BASE from '@/helpers/api'

export default {
  name: 'ClientesView',
  data() {
    return {
      treeItems: [],
      gabinetesSeleccionados: [],
      proyectoSeleccionado: false,
      open: [],
      cargandoTabla: false,
      gabReqId: 0,
      cacheAreas: Object.create(null),
      cacheProyectos: Object.create(null),
      cacheGabinetes: Object.create(null),
      headers: [
        { text: 'Tag', value: 'tag' },
        { text: 'Tipo', value: 'type' },
        { text: 'Proyecto', value: 'project_code' },
        { text: 'Cliente', value: 'client_name' },
      ],
    }
  },
  async created() {
    const { data: clients } = await axios.get(`${API_BASE}/clients`);
    this.treeItems = [{
      id: 'root',
      name: 'CLIENTES',
      type: 'root',
      children: clients.map(c => ({
        id: `client-${c.id}`,
        name: c.name,
        type: 'client',
        clientId: c.id,
        children: []
      }))
    }];
  },
  computed: {
    paneHeight() {
      return this.$vuetify?.breakpoint?.mdAndUp ? 'calc(100vh - 220px)' : '60vh';
    },
    treeInlineStyle() {
      return { height: this.paneHeight, overflowY: 'auto' };
    }
  },
  methods: {
    async loadChildren(item) {
      if (item.type === 'client') {
        if (this.cacheAreas[item.clientId]) {
          item.children = this.cacheAreas[item.clientId];
          return;
        }
        const { data: areas } = await axios.get(`${API_BASE}/clients/${item.clientId}/areas`);
        item.children = areas.map(a => ({
          id: `area-${a.id}`,
          name: a.name,
          type: 'area',
          clientId: item.clientId,
          areaId: a.id,
          children: []
        }));
        this.cacheAreas[item.clientId] = item.children;
      }
      if (item.type === 'area') {
        if (this.cacheProyectos[item.areaId]) {
          item.children = this.cacheProyectos[item.areaId];
          return;
        }
        const { data: projects } = await axios.get(`${API_BASE}/clients/${item.clientId}/areas/${item.areaId}/projects`);
        item.children = projects.map(p => ({
          id: `proj-${p.id}`,
          name: p.project_code || p.name || `Proyecto ${p.id}`,
          type: 'project',
          clientId: item.clientId,
          areaId: item.areaId,
          projectId: p.id,
        }));
        this.cacheProyectos[item.areaId] = item.children;
      }
    },
    getIcon(item, open) {
      switch (item.type) {
        case 'root': return open ? 'mdi-folder-open' : 'mdi-folder';
        case 'client': return 'mdi-account-group';
        case 'area': return 'mdi-domain';
        case 'project': return 'mdi-clipboard-list';
        case 'cabinet': return 'mdi-air-filter';
        default: return 'mdi-file';
      }
    },
    onOpenUpdate(open) { this.open = open; },
    projectKey(item) {
      return item.type === 'project' ? `${item.clientId}-${item.areaId}-${item.projectId}` : '';
    },
    onProjectClick(item) {
      if (item.type !== 'project') return;
      const key = this.projectKey(item);
      const now = Date.now();
      if (key === this.lastFetchKey && now - this.lastFetchTime < 500) return;
      this.lastFetchKey = key;
      this.lastFetchTime = now;
      this.fetchGabinetesDelProyecto(item.clientId, item.areaId, item.projectId);
    },
    async fetchGabinetesDelProyecto(clienteId, areaId, projectId) {
      const reqId = ++this.gabReqId;
      this.selectedProjectKey = `${clienteId}-${areaId}-${projectId}`;
      this.proyectoSeleccionado = true;

      if (this.cacheGabinetes[projectId]) {
        this.gabinetesSeleccionados = this.decorateGabinetes(this.cacheGabinetes[projectId], clienteId, areaId, projectId);
        return;
      }

      this.cargandoTabla = true;
      try {
        const { data } = await axios.get(`${API_BASE}/gabinetes`, { params: { project_id: projectId } });
        if (reqId !== this.gabReqId) return;
        this.cacheGabinetes[projectId] = data;
        this.gabinetesSeleccionados = this.decorateGabinetes(data, clienteId, areaId, projectId);
      } finally {
        if (reqId === this.gabReqId) this.cargandoTabla = false;
      }
    },
    decorateGabinetes(data, clienteId, areaId, projectId) {
      const clienteNode = this.treeItems?.[0]?.children.find(c => c.clientId === clienteId);
      const areaNode = clienteNode?.children?.find(a => a.areaId === areaId);
      const proyectoNode = areaNode?.children?.find(p => p.projectId === projectId);
      return data.map(g => ({
        id: `cab-${g.id}`,
        tag: g.tag,
        type: g.type || 'cabinet',
        project_code: proyectoNode?.name || '',
        client_name: clienteNode?.name || '',
        clientId: clienteId,
        areaId,
        projectId,
        name: g.tag,
      }));
    },
    volverInicio() {
      this.$router.push("/inicio").catch(() => {});
    },
    onRowClick(item) {
      const key = item.id || `${item.tag}-${item.projectId}`;
      const now = Date.now();
      if (key === this.lastRowClickKey && now - this.lastRowClickTime < 600) return;
      this.lastRowClickKey = key;
      this.lastRowClickTime = now;

      const tag = encodeURIComponent(item.tag || item.name || '');
      const href = this.$router.resolve({ path: `/gabinetes/catalogo/tag/${tag}` }).href;
      window.open(href.startsWith('http') ? href : `${window.location.origin}${href}`, '_blank', 'noopener');
    }
  }
}
</script>

<style scoped>
.global-dashboard-card {
  border-radius: 16px; /* Bordes redondeados */
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: background-color 0.3s, color 0.3s;
}

/* Tema claro */
.theme--light .global-dashboard-card {
  background-color: #ffffff;
  color: #1e1e1e;
}

/* Tema oscuro */
.theme--dark .global-dashboard-card {
  background-color: #1e1e1e; /* gris oscuro */
  color: #f5f5f5;
  box-shadow: 0 4px 20px rgba(0,0,0,0.6);
}

/* DataTable */
.white-card {
  border-radius: 12px;
  transition: background-color 0.3s, color 0.3s;
}

/* Tablas en claro */
.theme--light .white-card {
  background-color: #ffffff !important;
  color: #1e1e1e !important;
}

/* Tablas en oscuro */
.theme--dark .white-card {
  background-color: #2a2a2a !important; /* un gris intermedio */
  color: #f5f5f5 !important;
}

/* Separador del árbol */
.left-pane {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
.theme--dark .left-pane {
  border-right: 1px solid rgba(255, 255, 255, 0.12);
}

/* Mantener textos legibles */
.theme--dark.v-application {
  background-color: transparent !important;
  color: #e0e0e0 !important;
}

/* Label proyecto: clic único y feedback */
.tree-label--project {
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}
.tree-label--project:hover {
  background-color: rgba(0, 0, 0, 0.06);
}
.theme--dark .tree-label--project:hover {
  background-color: rgba(255, 255, 255, 0.08);
}
.tree-label--project.tree-label--loading {
  opacity: 0.85;
  pointer-events: none;
}

</style>
